// Pure parsing + origin validation for ServiceTitan scheduler postMessages.
// No React, no side effects — safe to unit-test in isolation later.
//
// The scheduler (https://scheduler.servicetitan.com) broadcasts a booking funnel
// to the parent window. The GA-shaped message carries the appointment id, e.g.:
//   { type: 'se-send-client-google-analytics',
//     ga: { action: 'BookingBooked', value: '1970494450', schedulerName: '…' } }

export const SCHEDULER_ORIGIN = 'https://scheduler.servicetitan.com';

export type ParsedBooking = {
  st_booking_id: string;
  scheduler: string;
};

// event.data may arrive as an object (structured clone) or a JSON string.
// Returns a plain object or null; never throws; arrays are not objects here.
function asObject(data: unknown): Record<string, unknown> | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

// Returns the booking iff this is a BookingBooked GA message from the scheduler
// origin AND it carries a non-empty appointment id; otherwise null.
export function parseBookingBooked(origin: string, data: unknown): ParsedBooking | null {
  if (origin !== SCHEDULER_ORIGIN) return null;

  const obj = asObject(data);
  if (!obj) return null;

  const ga = obj.ga;
  if (!ga || typeof ga !== 'object') return null;
  const gaObj = ga as Record<string, unknown>;
  if (gaObj.action !== 'BookingBooked') return null;

  const id = gaObj.value;
  if (typeof id !== 'string' && typeof id !== 'number') return null;
  if (typeof id === 'number' && !Number.isFinite(id)) return null;
  const st_booking_id = String(id);
  if (st_booking_id === '') return null;
  const scheduler = typeof gaObj.schedulerName === 'string' ? gaObj.schedulerName : '';

  return { st_booking_id, scheduler };
}
